import {
  Box,
  Stack,
  Typography,
  Button,
  useTheme,
  Menu,
  MenuItem,
  Select,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  Snackbar,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ltcpRoutes from "../constants/ltcpRoutes";
import { useContext, useEffect, useState } from "react";
import { LTCPContext } from "../LTCPContext";
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";
import {
  getAlertBranchFactoryMap,
  getAlertFilters,
  getAlertThreshold,
  getCapacityFilters,
  getCuFilters,
  getDemandFilters,
  getPipelineRunFilters,
  getRunDetails,
  getSimulationFilters,
  getUserRole,
} from "../api/ltcpBackend.js";
import { useMsal } from "@azure/msal-react";

function LandingPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const activeAccount = accounts[0];
  const userEmail = activeAccount.username;

  const [firstRunDone, setFirstRunDone] = useState(false);
  const [demandSelectMenu, setDemandSelectMenu] = useState(null);
  const [simulationSelectMenu, setSimulationSelectMenu] = useState(null);
  const [notAllowed, setNotAllowed] = useState(false);
  const [defaultFilterValue, setDefaultFilterValue] = useState({});

  const {
    productType,
    setProductType,
    allRunDetails,
    setAllRunDetails,
    runDetails,
    setRunDetails,
    runTimeOptions,
    setRunTimeOptions,
    currentUserRole,
    setCurrentUserRole,
    runtype,
    runtime,
    setRuntype,
    setRuntime,
    setLoading,
    demandFile,
    setDemandFile,
    setCuFilters,
    setAlertFilters,
    setDemandFilters,
    setCapacityFilters,
    setBranchFactoryMap,
    setSimulationFilters,
    thresholdLevel,
    setThresholdLevel,
    setAcceptFeature,
    demandOptions,
    setDemandOptions,
    loadPipelineStatusFilter,
  } = useContext(LTCPContext);

  // Change the selected runtype value and according to that it will change
  // the product type, demand type and runtime values.
  function changeRunType(val) {
    let run_details = allRunDetails[val];
    let new_prod_type = Object.keys(run_details)[0];
    let demand_options = run_details[new_prod_type];
    let new_demand_type = "V1Demand";
    let run_times = demand_options[new_demand_type];

    setRuntype(val);
    setProductType(new_prod_type);
    setDemandFile(new_demand_type);
    setRuntime(run_times[0]);

    setRunDetails(run_details);
    setDemandOptions(demand_options);
    setRunTimeOptions(run_times);

    if (val === "Schedule") {
      setProductType(new_prod_type);
    } else {
      setProductType(new_prod_type);
    }
  }

  // Change the product type value and according to that it will change the
  // demand type and runtime values.
  function changeProductType(val) {
    let demand_options = runDetails[val];
    let default_demand_type = "V1Demand";
    let run_times = demand_options[default_demand_type];

    setProductType(val);
    setDemandFile(default_demand_type);
    setDemandOptions(demand_options);
    setRunTimeOptions(run_times);
    setRuntime(run_times[0]);
  }

  // Change the demand type value and according to that it will change the
  // runtime values.
  function changeDemandType(val) {
    let run_times = demandOptions[val];

    setDemandFile(val);
    setRunTimeOptions(run_times);
    setRuntime(run_times[0]);
  }

  // It just change the runtime value. No cascading filters are there after this.
  function changeRunTime(val) {
    setRuntime(val);
  }

  // Call the api to get the branch factory mapping that and store it in the
  // context that will be use in alert page filter.
  async function fetchAlertBranchFactoryMap() {
    const data = await getAlertBranchFactoryMap();
    setBranchFactoryMap(data);
  }

  // Call the api to get the cu threshold level and store it in the context
  // that will be use in multiple pages.
  async function fetchThresholdLevel() {
    const data = await getAlertThreshold();
    setThresholdLevel(data);
  }

  // Call multiple apis asynchonasly to get filter values for different pages
  // and store them in the context. This one also collect the run details and
  // user detials and store them in the context.
  async function fetchFilters() {
    setLoading(true);

    const init_resp = await Promise.all([
      getUserRole(userEmail),
      getRunDetails(userEmail),
      fetchAlertBranchFactoryMap(),
      fetchThresholdLevel(),
      getPipelineRunFilters(userEmail),
    ]);

    if (init_resp[1] === 401) {
      setLoading(false);
      return setNotAllowed(true);
    }

    const role = init_resp[0];
    const all_run_details = init_resp[1].filters;
    const pipeline_run_filters = init_resp[4];

    if (!role) {
      setLoading(false);
      return setNotAllowed(true);
    } else setCurrentUserRole(role);

    let run_details;
    let demand_types;
    let run_times;

    let default_product_type = init_resp[1].default.producttype;
    let default_demand_type = init_resp[1].default.demandtype;
    let default_run_type = init_resp[1].default.runtype;
    let default_run_time = init_resp[1].default.runtime;

    loadPipelineStatusFilter(pipeline_run_filters);

    setDefaultFilterValue({
      productType: default_product_type,
      demandType: default_demand_type,
      runtype: default_run_type,
      runtime: default_run_time,
    });

    try {
      run_details = all_run_details[default_run_type];
      demand_types = run_details[default_product_type];
      run_times = demand_types[default_demand_type];
    } catch {
      run_details = {};
      demand_types = {};
      run_times = [];
    }

    setAllRunDetails(all_run_details);
    setRunDetails(run_details);
    setDemandOptions(demand_types);
    setRunTimeOptions(run_times);

    if (default_run_type === "Schedule") {
      setProductType(default_product_type);
    } else {
      setProductType(default_product_type);
    }

    setDemandFile(default_demand_type);
    setRuntime(default_run_time);
    setRuntype(default_run_type);
    setAcceptFeature(true);

    if (!role.mdm) {
      const resp = await Promise.all([
        getCuFilters(userEmail, {
          runtype: default_run_type,
          runtime: default_run_time,
          demandFile: default_demand_type,
        }),
        getDemandFilters(userEmail, {
          runtype: default_run_type,
          runtime: default_run_time,
          demandFile: default_demand_type,
          time_gran: "MoM",
        }),
        getCapacityFilters(userEmail, {
          runtype: default_run_type,
          runtime: default_run_time,
        }),
        getAlertFilters(userEmail, {
          runtype: default_run_type,
          runtime: default_run_time,
          demand_file: default_demand_type,
          productType: default_product_type,
        }),
        getSimulationFilters(userEmail, {
          runtime: default_run_time,
          runtype: default_run_type,
          demand_file: default_demand_type,
        }),
      ]);

      if (
        resp[0] === 401 ||
        resp[1] === 401 ||
        resp[2] === 401 ||
        resp[3] === 401 ||
        resp[4] === 401
      ) {
        setLoading(false);
        return setNotAllowed(true);
      }

      setCuFilters(resp[0]);
      setDemandFilters(resp[1]);
      setCapacityFilters(resp[2]);
      setAlertFilters(resp[3]);
      setSimulationFilters(resp[4]);
    }

    setFirstRunDone(true);
    setLoading(false);
  }

  // Call multiple apis asynchonasly to get filter values for different pages
  // and store them in the context. This only run from the second time when
  // user change any drop down value and as we already have user details it
  // doesn't call them again.
  async function refetchFilters() {
    if (!firstRunDone) return;

    setAcceptFeature(
      productType === defaultFilterValue.productType &&
        runtime === defaultFilterValue.runtime &&
        runtype === defaultFilterValue.runtype &&
        demandFile === defaultFilterValue.demandType
    );

    setLoading(true);
    try {
      if (!currentUserRole.mdm) {
        const resp = await Promise.all([
          getCuFilters(userEmail, {
            runtype,
            runtime,
            demandFile,
          }),
          getDemandFilters(userEmail, {
            runtype,
            runtime,
            demandFile,
            time_gran: "MoM",
          }),
          getCapacityFilters(userEmail, {
            runtype,
            runtime,
          }),
          getAlertFilters(userEmail, {
            runtype,
            runtime,
            demand_file: demandFile,
            productType,
          }),
          getSimulationFilters(userEmail, {
            runtype,
            runtime,
            demand_file: demandFile,
          }),
        ]);

        if (
          resp[0] === 401 ||
          resp[1] === 401 ||
          resp[2] === 401 ||
          resp[3] === 401 ||
          resp[4] === 401
        ) {
          setLoading(false);
          return setNotAllowed(true);
        }

        setCuFilters(resp[0]);
        setDemandFilters(resp[1]);
        setCapacityFilters(resp[2]);
        setAlertFilters(resp[3]);
        setSimulationFilters(resp[4]);
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    refetchFilters();
  }, [productType, runtime, runtype, demandFile]);

  return (
    <Box
      top={0}
      left={0}
      right={0}
      bottom={0}
      position={"fixed"}
      width="100vw"
      height="100vh"
      display="flex"
      alignItems="stretch"
      bgcolor={theme.palette.primary.light}
      sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <Stack width="60%" justifyContent="center" alignItems="center">
        <Box
          gap={4}
          display="flex"
          flexDirection="column"
          justifyContent="start"
          alignItems="start"
        >
          <Typography
            fontWeight="bold"
            fontSize={56}
            sx={{ fontFamily: "arial !important" }}
          >
            Capacity Planning
          </Typography>
          <Stack gap={2}>
            <Stack gap={2} direction="row">
              <Stack>
                <Typography fontSize={12}>Run Type</Typography>
                <Select
                  size="small"
                  sx={{ width: 150 }}
                  value={runtype}
                  onChange={(e) => changeRunType(e.target.value)}
                >
                  {Object.keys(allRunDetails)
                    .sort((a, b) => {
                      if (a === "Schedule") return -1;
                      else return 1;
                    })
                    .map((item, index) => (
                      <MenuItem value={item} key={index}>
                        {item}
                      </MenuItem>
                    ))}
                </Select>
              </Stack>
              <Stack>
                <Typography fontSize={12}>Product Type</Typography>
                <Select
                  size="small"
                  sx={{ width: 150 }}
                  value={productType}
                  onChange={(e) => changeProductType(e.target.value)}
                >
                  {/* {runtype === "Schedule" && (
                    <MenuItem value="All">All</MenuItem>
                  )} */}
                  {Object.keys(runDetails).map((item, index) => (
                    <MenuItem value={item} key={index}>
                      {item.replaceAll("_", " ")}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
              <Stack>
                <Typography fontSize={12}>Demand Type</Typography>
                <Select
                  size="small"
                  sx={{ width: 150 }}
                  value={demandFile}
                  onChange={(e) => changeDemandType(e.target.value)}
                >
                  {Object.keys(demandOptions)
                    .sort((a, b) => {
                      if (a === "V1Demand") return -1;
                      else return 1;
                    })
                    .map((item, index) => (
                      <MenuItem value={item} key={index}>
                        {item.replaceAll("_", " ")}
                      </MenuItem>
                    ))}
                </Select>
              </Stack>
              <Stack>
                <Typography fontSize={12}>Run Time</Typography>
                <Select
                  size="small"
                  sx={{ width: 150 }}
                  value={runtime}
                  onChange={(e) => changeRunTime(e.target.value)}
                >
                  {runTimeOptions.map((item, index) => (
                    <MenuItem key={index} value={item}>
                      {new Date(item).toLocaleString()}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
            </Stack>
            <Stack gap={2} direction="row">
              <Button
                variant="contained"
                sx={{ width: 150 }}
                onClick={() => navigate(ltcpRoutes.DASHBOARD)}
                disabled={currentUserRole.mdm}
              >
                Dashboard
                {/* <ArrowDropDownIcon /> */}
              </Button>
              {/* <Menu
                open={Boolean(demandSelectMenu)}
                anchorEl={demandSelectMenu}
                onClose={() => setDemandSelectMenu(null)}
              >
                <MenuItem
                  sx={{ background: "#C5EAFB", mx: 1, fontSize: 14 }}
                  onClick={() => {
                    setDemandFile("V1Demand");
                    navigate(ltcpRoutes.DASHBOARD);
                  }}
                >
                  Dashboard - V1 demand file
                </MenuItem>
                <MenuItem
                  sx={{ background: "#C5EAFB", mx: 1, fontSize: 14, my: 0.5 }}
                  onClick={() => {
                    setDemandFile("M1Demand");
                    navigate(ltcpRoutes.DASHBOARD);
                  }}
                >
                  Dashboard - Manual 1 demand file
                </MenuItem>
                <MenuItem
                  sx={{ background: "#C5EAFB", mx: 1, fontSize: 14 }}
                  onClick={() => {
                    setDemandFile("M2Demand");
                    navigate(ltcpRoutes.DASHBOARD);
                  }}
                >
                  Dashboard - Manual 2 demand file
                </MenuItem>
              </Menu> */}
              <Button
                variant="contained"
                sx={{ width: 150 }}
                onClick={() => {
                  navigate(ltcpRoutes.UPLOADS);
                  localStorage.removeItem("productType");
                }}
                disabled={!(currentUserRole.write || currentUserRole.mdm)}
              >
                Uploads
              </Button>
            </Stack>
            <Stack gap={2} direction="row">
              <Button
                variant="contained"
                sx={{ width: 150 }}
                disabled={currentUserRole.mdm || !currentUserRole.write}
                onClick={() => navigate(ltcpRoutes.SIMULATION)}
              >
                Simulation
                {/* <ArrowDropDownIcon /> */}
              </Button>
              {/* <Menu
                open={Boolean(simulationSelectMenu)}
                anchorEl={simulationSelectMenu}
                onClose={() => setSimulationSelectMenu(null)}
              >
                <MenuItem
                  sx={{ background: "#C5EAFB", mx: 1, fontSize: 14 }}
                  onClick={() => {
                    setDemandFile("V1Demand");
                    navigate(ltcpRoutes.SIMULATION);
                  }}
                >
                  Simulation - V1 demand file
                </MenuItem>
                <MenuItem
                  sx={{ background: "#C5EAFB", mx: 1, fontSize: 14, my: 0.5 }}
                  onClick={() => {
                    setDemandFile("M1Demand");
                    navigate(ltcpRoutes.SIMULATION);
                  }}
                >
                  Simulation - Manual 1 demand file
                </MenuItem>
                <MenuItem
                  sx={{ background: "#C5EAFB", mx: 1, fontSize: 14 }}
                  onClick={() => {
                    setDemandFile("M2Demand");
                    navigate(ltcpRoutes.SIMULATION);
                  }}
                >
                  Simulation - Manual 2 demand file
                </MenuItem>
              </Menu> */}
              <Button
                variant="contained"
                sx={{ width: 150 }}
                onClick={() => navigate(ltcpRoutes.REPORTS)}
                disabled={currentUserRole.mdm}
              >
                Reports
              </Button>
            </Stack>
            <Stack gap={2} direction="row">
              <Button
                variant="contained"
                sx={{ width: 150 }}
                onClick={() => navigate(ltcpRoutes.ROLES)}
                disabled={!currentUserRole.role}
              >
                Roles
              </Button>
            </Stack>
          </Stack>
        </Box>
        <img
          src="/cp_illustration.png"
          style={{
            position: "absolute",
            height: "250px",
            width: "480px",
            objectFit: "scale-down",
            right: "200px",
            bottom: "30px",
            zIndex: 99,
          }}
        />
      </Stack>
      <Stack
        width="40%"
        sx={{
          position: "relative",
          background: "#1F36C7",
          clipPath: "polygon(25% 0%, 100% 0%, 100% 100%, 0% 100%)",
        }}
        alignItems="end"
      >
        <img
          src="/unilever_logo.svg"
          height={48}
          style={{ position: "absolute", top: 25, right: 25 }}
        />
      </Stack>
      <Snackbar
        open={notAllowed}
        autoHideDuration={10000}
        anchorOrigin={{ horizontal: "center", vertical: "top" }}
        onClose={() => {
          setNotAllowed(false);
          navigate(ltcpRoutes.HOME);
        }}
      >
        <Alert
          severity="warning"
          onClose={() => {
            setNotAllowed(false);
            navigate(ltcpRoutes.HOME);
          }}
        >
          You don't have permission to access LTCP module.
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default LandingPage;
